import React, { Component } from 'react'
import PropTypes from 'prop-types'
import produce from 'immer'
import { compose } from 'redux'
import { withRouter } from 'react-router-dom'
import { connect } from 'react-redux'
import {
  getFormattedAttrId,
  reIndexResponses,
} from '@edulastic/common/src/helpers'
import {
  cloneDeep,
  find,
  isUndefined,
  isArray,
  isEmpty,
  difference,
} from 'lodash'

import { withTheme } from 'styled-components'
import { withNamespaces } from '@edulastic/localization'
import { LANGUAGE_EN } from '@edulastic/constants/const/languages'
import { notification } from '@edulastic/common'
import LanguageContext from '@edulastic/common/src/contexts/LanguageContext'
import { questionType } from '@edulastic/constants'
import QuestionTextArea from '../../components/QuestionTextArea'
import { updateVariables } from '../../utils/variables'
import { setQuestionDataAction } from '../../../author/QuestionEditor/ducks'
import { Subtitle } from '../../styled/Subtitle'
import { getLanguageKeys } from '../../../common/utils/helpers'
import {
  getCurrentLanguage,
  getLanguageTabsSelector,
} from '../../../common/components/LanguageSelectorTab/duck'
import ClearContentConfirmationModal from '../../components/ConfirmClearStimulusModal'
import { getCurrentQuestionSelector } from '../../../author/sharedDucks/questions'
import { INITIAL_FIELD_VALUES_BY_QTYPE } from '../../constants/constantsForQuestions'
import { initializeLanguageObj } from '../../utils/question'

const INITIAL_FIELD_VALUES =
  INITIAL_FIELD_VALUES_BY_QTYPE[questionType.CLOZE_DRAG_DROP]

class TemplateMarkup extends Component {
  static propTypes = {
    t: PropTypes.func.isRequired,
    item: PropTypes.object.isRequired,
    setQuestionData: PropTypes.func.isRequired,
  }

  constructor(props) {
    super(props)
    this.state = {
      showClearContentModal: false,
      prevItem: {},
    }
  }

  toggleShowClearContentModal = () => {
    this.setState((prevState) => ({
      ...prevState,
      showClearContentModal: !prevState.showClearContentModal,
    }))
  }

  setTempContent = (prevItem = {}) => {
    this.setState((prevState) => ({
      ...prevState,
      prevItem: cloneDeep(prevItem),
    }))
  }

  handleModalResponse = (response) => {
    const { setQuestionData, item } = this.props
    const { prevItem } = this.state
    if (response === 'clear') {
      // Reset the stimulus and options
      setQuestionData(
        produce(item, (draft) => {
          draft.stimulus = ''
          draft.responseIds = []
          draft.options = []
          draft.validation = {
            validResponse: { value: [] },
            altResponses: [],
          }
          getLanguageKeys(draft).forEach((lang) => {
            const languageFeature = draft?.languageFeatures?.[lang] || {}
            if (!isEmpty(languageFeature)) {
              draft.languageFeatures[lang].stimulus = ''
              draft.languageFeatures[lang].options = []
            }
          })
          updateVariables(draft)
        }),
        LANGUAGE_EN
      )
      this.toggleShowClearContentModal()
      this.setTempContent()
    } else {
      if (!isEmpty(prevItem)) {
        setQuestionData({ ...prevItem }, LANGUAGE_EN)
      }
      this.toggleShowClearContentModal()
      this.setTempContent()
    }
  }

  onChangeMarkUp = (stimulus) => {
    try {
      const { item, setQuestionData, questionData } = this.props

      const reduceResponse = (tmpl, preIDs, preValidation) => {
        const _responseIds = []
        const newValue = []
        let newAltValue = []

        const temp = tmpl || ''
        const _preIDs = cloneDeep(preIDs)
        const _preValidation = cloneDeep(preValidation)
        const {
          validResponse: { value },
          altResponses,
        } = cloneDeep(_preValidation)

        newAltValue = cloneDeep(altResponses)

        if (!window.$) {
          return { responseIds: _responseIds, validation: _preValidation }
        }

        const parsedHTML = $('<div />').html(temp)
        $(parsedHTML)
          .find('response')
          .each(function (index) {
            const id = $(this).attr('id')
            const { index: preIndex } =
              find(_preIDs, (response) => response.id === id) || {}

            if (!isUndefined(preIndex)) {
              newValue[index] = value[preIndex]
            } else {
              newValue[index] = null
            }

            if (isArray(altResponses)) {
              altResponses.map((altResponse, altIndex) => {
                if (!isUndefined(preIndex)) {
                  newAltValue[altIndex].value[index] =
                    altResponse.value[preIndex]
                } else {
                  newAltValue[altIndex].value[index] = null
                }
                return null
              })
            }

            _responseIds.push({ index, id })
          })

        _preValidation.validResponse.value = newValue
        if (isArray(newAltValue)) {
          _preValidation.altResponses = newAltValue
        }
        return { responseIds: _responseIds, validation: _preValidation }
      }

      const {
        item: { stimulus: prevStimulus = '', validation: prevValidation = {} },
        currentLanguage,
        languageTabs = [],
      } = this.props

      /**
       * getLanguageKeys is a helper function to get the language keys of the current item
       */
      const languageKeys = getLanguageKeys(item) || []

      /**
       * when stimulus is empty we need to set the primary language as the first language
       * when stimulus is not empty we need to set the primary language as the English
       */
      const [primaryLanguage] = languageTabs || [LANGUAGE_EN]

      const { responseIds: prevResponseIds = [] } = reduceResponse(
        prevStimulus,
        [],
        prevValidation
      )

      const { responseIds = [] } = reduceResponse(stimulus, [], prevValidation)

      /**
       * differenceOfResponseBoxCount is the difference of response box count between the previous stimulus and the current stimulus
       */
      const differenceOfResponseBoxCount =
        prevResponseIds?.length - responseIds?.length

      /**
       * isNewStimulusEmpty is a flag to check if the new stimulus is empty and the previous stimulus is not empty
       * and the difference of response box count is not 0
       */
      const isNewStimulusEmpty =
        differenceOfResponseBoxCount !== 0 &&
        (prevStimulus || '').trim().length &&
        !(stimulus || '').trim().length
      /**
       * when currentLanguage is not the primary language and the difference of response box count is not 0
       * we need to show the clear content modal
       */
      if (
        currentLanguage !== primaryLanguage &&
        differenceOfResponseBoxCount !== 0
      ) {
        /**
         * When stimulus is empty user is trying to remove all content + response boxes
         */
        if (isNewStimulusEmpty) {
          this.toggleShowClearContentModal(true)
          this.setTempContent(questionData)
          /** empty the stimulus for state update, require to update the component */
          setQuestionData(
            produce(questionData, (draft) => {
              draft.stimulus = ''

              languageKeys.forEach((lang) => {
                if (draft.languageFeatures[lang]) {
                  draft.languageFeatures[lang].stimulus = ''
                }
              })
            }),
            LANGUAGE_EN
          )
          return
        }
        /**
         * Here we return because response boxes are changed but stimulus is still present
         * It means user is trying to remove the response in other language tab (not primary),
         * No change FroalaEditor is reverting response box changes
         */
        notification({
          type: 'warn',
          messageKey: 'warnChangeInResponseBoxes',
        })
        return
      }

      /** Removes response boxes */
      if (differenceOfResponseBoxCount > 0) {
        const removedResponseIds = difference(
          prevResponseIds.map((response) => response.id),
          responseIds.map((response) => response.id)
        )
        setQuestionData(
          produce(questionData, (draft) => {
            if (primaryLanguage === LANGUAGE_EN) {
              draft.stimulus = stimulus
              const { responseIds: _responseIds, validation } = reduceResponse(
                stimulus,
                draft.responseIds,
                draft.validation
              )
              draft.responseIds = _responseIds
              draft.validation = validation
            } else {
              initializeLanguageObj(
                draft,
                primaryLanguage,
                INITIAL_FIELD_VALUES
              )

              draft.languageFeatures[primaryLanguage].stimulus = stimulus
              const { responseIds: _responseIds, validation } = reduceResponse(
                stimulus,
                draft.responseIds,
                draft.validation
              )
              draft.responseIds = _responseIds
              draft.validation = validation
            }
            languageKeys
              .filter((language) => language !== primaryLanguage)
              .forEach((lang) => {
                initializeLanguageObj(draft, lang, INITIAL_FIELD_VALUES)

                const languageStimulus =
                  draft.languageFeatures[lang].stimulus || ''
                const parsedHTML = $('<div />').html(languageStimulus)
                if (languageStimulus) {
                  removedResponseIds.forEach((responseId) => {
                    parsedHTML.find(`response[id="${responseId}"]`).remove()
                  })
                }
                draft.languageFeatures[lang].stimulus = reIndexResponses(
                  parsedHTML.html() || ''
                )
              })

            updateVariables(draft)
          }),
          LANGUAGE_EN
        )
        return
      }

      /** 2. Adds response boxes */
      if (differenceOfResponseBoxCount < 0) {
        const newResponses = difference(
          responseIds.map((response) => response.id),
          prevResponseIds.map((response) => response.id)
        )

        setQuestionData(
          produce(questionData, (draft) => {
            if (primaryLanguage === LANGUAGE_EN) {
              draft.stimulus = stimulus

              const { responseIds: _responseIds, validation } = reduceResponse(
                stimulus,
                draft.responseIds,
                draft.validation
              )
              draft.responseIds = _responseIds
              draft.validation = validation
            } else {
              initializeLanguageObj(
                draft,
                primaryLanguage,
                INITIAL_FIELD_VALUES
              )

              draft.languageFeatures[primaryLanguage].stimulus = stimulus
              const { responseIds: _responseIds, validation } = reduceResponse(
                stimulus,
                draft.responseIds,
                draft.validation
              )
              draft.responseIds = _responseIds
              draft.validation = validation
            }
            languageKeys
              .filter((language) => language !== primaryLanguage)
              .forEach((lang) => {
                initializeLanguageObj(draft, lang, INITIAL_FIELD_VALUES)

                let languageStimulus =
                  draft.languageFeatures[lang].stimulus || ''

                newResponses.forEach((responseId) => {
                  languageStimulus += ` <response id="${responseId}" contenteditable="false" key="${responseId}" ></response> `
                })

                draft.languageFeatures[lang].stimulus = languageStimulus
              })
            updateVariables(draft)
          }),
          LANGUAGE_EN
        )
        return
      }

      /** 3. No change in response boxes */
      setQuestionData(
        produce(item, (draft) => {
          draft.stimulus = stimulus
          const { responseIds: _responseIds, validation } = reduceResponse(
            stimulus,
            draft.responseIds,
            draft.validation
          )
          draft.responseIds = _responseIds
          draft.validation = validation
          updateVariables(draft)
        })
      )
    } catch (error) {
      console.log('Error in onChangeMarkUp:', error)
    }
  }

  render() {
    const {
      t,
      item,
      froalaFooter,
      currentLanguage,
      languageTabs = [],
    } = this.props
    const { showClearContentModal } = this.state

    return (
      <>
        <Subtitle
          id={getFormattedAttrId(
            `${item?.title}-${t('component.cloze.dragDrop.composequestion')}`
          )}
        >
          {t('component.cloze.dragDrop.composequestion')}
        </Subtitle>

        <LanguageContext.Provider value={{ currentLanguage, languageTabs }}>
          <QuestionTextArea
            placeholder={t('component.cloze.dragDrop.thisisstem')}
            onChange={this.onChangeMarkUp}
            additionalToolbarOptions={['response']}
            toolbarId="template-markup-area"
            value={item.stimulus}
            border="border"
            froalaFooter={froalaFooter}
          />
        </LanguageContext.Provider>
        <ClearContentConfirmationModal
          handleModalResponse={this.handleModalResponse}
          showClearContentModal={showClearContentModal}
          primaryLanguage={languageTabs[0]}
        />
      </>
    )
  }
}

const enhance = compose(
  withRouter,
  withNamespaces('assessment'),
  withTheme,
  connect(
    (state) => ({
      currentLanguage: getCurrentLanguage(state),
      languageTabs: getLanguageTabsSelector(state),
      questionData: getCurrentQuestionSelector(state),
    }),
    { setQuestionData: setQuestionDataAction }
  )
)

export default enhance(TemplateMarkup)
